import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { RefreshCw, ExternalLink, Receipt, CreditCard, Landmark } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FULFILL_OPTIONS = [
  { value: 'aguardando_pagamento', label: 'Aguardando pagamento' },
  { value: 'em_producao', label: 'Em produção' },
  { value: 'em_preparacao', label: 'Em preparação' },
  { value: 'enviado', label: 'Enviado' },
  { value: 'entregue', label: 'Entregue' },
  { value: 'cancelado', label: 'Cancelado' },
];

const STATUS_LABEL = {
  approved: { label: 'Aprovado', class: 'bg-emerald-100 text-emerald-800' },
  pending: { label: 'Pendente', class: 'bg-amber-100 text-amber-800' },
  in_process: { label: 'Em análise', class: 'bg-amber-100 text-amber-800' },
  rejected: { label: 'Recusado', class: 'bg-red-100 text-red-800' },
  cancelled: { label: 'Cancelado', class: 'bg-neutral-200 text-neutral-700' },
  refunded: { label: 'Estornado', class: 'bg-neutral-200 text-neutral-700' },
};

const AdminOrders = () => {
  usePageTitle('Pedidos');
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchOrders();
  }, [authLoading, user]); // eslint-disable-line react-hooks/exhaustive-deps -- fetchOrders/navigate estáveis

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/payments/orders`);
      setOrders(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const updateFulfillment = async (orderId, value) => {
    try {
      const res = await axios.put(`${API_URL}/payments/orders/${orderId}/fulfillment`, { fulfillment_status: value });
      setOrders((prev) => prev.map((o) => (o.order_id === orderId ? { ...o, ...res.data } : o)));
      toast.success('Status atualizado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar status');
    }
  };

  const totalRevenue = orders
    .filter((o) => o.payment_status === 'approved')
    .reduce((s, o) => s + Number(o.amount || 0), 0);

  return (
    <div data-testid="admin-orders-page" className="min-h-screen bg-white pt-28 pb-16 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="font-accent text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-2">
              Admin · Pagamentos
            </p>
            <h1 className="font-heading text-4xl font-light text-neutral-900">Pedidos</h1>
          </div>
          <button
            data-testid="refresh-orders"
            onClick={fetchOrders}
            className="inline-flex items-center gap-2 border border-neutral-300 px-5 py-2 text-xs uppercase tracking-widest hover:bg-neutral-100"
          >
            <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
            Atualizar
          </button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="border border-neutral-200 p-6">
            <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-widest mb-3">
              <Receipt className="w-4 h-4" strokeWidth={1.5} /> Total de pedidos
            </div>
            <p className="font-heading text-3xl">{orders.length}</p>
          </div>
          <div className="border border-neutral-200 p-6">
            <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-widest mb-3">
              <CreditCard className="w-4 h-4" strokeWidth={1.5} /> Pagos
            </div>
            <p className="font-heading text-3xl">
              {orders.filter((o) => o.payment_status === 'approved').length}
            </p>
          </div>
          <div className="border border-neutral-200 p-6">
            <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-widest mb-3">
              <Landmark className="w-4 h-4" strokeWidth={1.5} /> Receita aprovada
            </div>
            <p className="font-heading text-3xl">
              R$ {totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <p className="text-sm text-neutral-500">Carregando...</p>
        ) : orders.length === 0 ? (
          <div className="border border-dashed border-neutral-300 py-16 text-center text-neutral-500">
            Nenhum pedido registrado ainda.
          </div>
        ) : (
          <div className="overflow-x-auto border border-neutral-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-50 text-xs uppercase tracking-widest text-neutral-500">
                  <th className="text-left px-4 py-3">Pedido</th>
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-left px-4 py-3">Tipo</th>
                  <th className="text-left px-4 py-3">Valor</th>
                  <th className="text-left px-4 py-3">Método</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Envio</th>
                  <th className="text-left px-4 py-3">Data</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const s = STATUS_LABEL[o.payment_status] || { label: o.payment_status || '—', class: 'bg-neutral-100 text-neutral-700' };
                  return (
                    <tr key={o.order_id} data-testid={`order-row-${o.order_id}`} className="border-t border-neutral-200 hover:bg-neutral-50">
                      <td className="px-4 py-3 font-mono text-xs">{o.order_id?.slice(0, 8)}</td>
                      <td className="px-4 py-3">
                        <div>{o.customer_name || '—'}</div>
                        <div className="text-xs text-neutral-500">{o.customer_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        {o.kind === 'quote_deposit' ? (
                          <span className="text-xs px-2 py-1 bg-neutral-900 text-white">Sinal {Math.round(o.deposit_percentage || 50)}%</span>
                        ) : (
                          <span className="text-xs px-2 py-1 bg-neutral-100">Compra direta</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        R$ {Number(o.amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-xs uppercase">{o.payment_method || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 ${s.class}`}>{s.label}</span>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          data-testid={`fulfillment-select-${o.order_id}`}
                          value={o.fulfillment_status || 'aguardando_pagamento'}
                          onChange={(e) => updateFulfillment(o.order_id, e.target.value)}
                          className="text-xs border border-neutral-300 px-2 py-1 focus:outline-none focus:border-neutral-900 bg-white"
                        >
                          {FULFILL_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-500">
                        {o.created_at ? new Date(o.created_at).toLocaleString('pt-BR') : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {o.checkout_url && (
                          <a
                            href={o.checkout_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-neutral-900 hover:underline"
                          >
                            Link <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrders;
